import { AxiosResponse } from 'axios';
import { useMutation } from 'react-query';

import { api } from '../../../http';
import { DefaultRes } from '../../../types';
import { queryClient } from '../../../services/react-query/react-query.client';
import { useGenericQuery } from '../../../services/react-query/generic.query';
import {
    ReEnrollRequest,
    ReportParams,
    RequestExitRequest,
    StatusListParams,
    TestModeKpiCounts,
    TestModeStatusPage,
    WhoTestedRow,
} from './testMode.types';
import {
    fixtureReportRows,
    fixtureStatusPage,
    testModeFixturesEnabled,
    FIXTURE_ROWS,
} from './testMode.fixtures';

// Read once at module load. In fixture mode the query hooks are still
// called (hook order stays stable across renders and HMR) but with
// enabled=false, so no network requests fire; the fixture data is
// substituted after the hook call.
const FIXTURES = testModeFixturesEnabled();

/**
 * Data access for the Test Mode tab (TM-8). All reads go through pom-2-api's
 * warehouse-backed endpoints (D14). NOTE: until the etl-service deploys the
 * `TestModeSessions` table to this environment, these return empty result
 * sets — the UI is built against the response shape, not live data.
 */

const STATUS_LIST_QUERY = 'test-mode-status-list';

/**
 * Live-monitoring polling cadences. During a training, an admin sits on this
 * tab watching completions come in — without polling, react-query's 5-minute
 * staleness would dominate the end-to-end lag (the warehouse ETL itself is
 * seconds when healthy, per Carlos). The summary polls at half the roster
 * cadence to bound per-tab backend load; both numbers are pending a load
 * sanity-check with Carlos for many-admins-during-a-training.
 */
const LIST_POLL_MS = 30_000;
const SUMMARY_POLL_MS = 60_000;

export const buildStatusListUrl = (params: StatusListParams): string => {
    const qs = new URLSearchParams();
    if (params.status && params.status !== 'all') qs.set('status', params.status);
    if (params.testCompleted !== undefined) qs.set('testCompleted', String(params.testCompleted));
    if (params.device) qs.set('device', params.device);
    if (params.name) qs.set('name', params.name);
    if (params.groupId) qs.set('groupId', params.groupId);
    if (params.teamId) qs.set('teamId', params.teamId);
    qs.set('page', String(params.page));
    qs.set('perPage', String(params.perPage));
    return `test-mode/status-list?${qs.toString()}`;
};

export function useTestModeStatusList(params: StatusListParams) {
    const query = useGenericQuery<TestModeStatusPage>(
        STATUS_LIST_QUERY,
        buildStatusListUrl(params),
        LIST_POLL_MS,
        undefined,
        true,
        undefined,
        !FIXTURES,
    );
    if (FIXTURES) {
        return { data: fixtureStatusPage(params), isLoading: false, isError: false };
    }
    return { data: query.data, isLoading: query.isLoading, isError: query.isError };
}

const SUMMARY_QUERY = 'test-mode-status-summary';

interface StatusSummaryResponse {
    inTestMode: number;
    completed: number;
    notTested: number;
    flagged: number;
}

/** KPI tile counts from GET /v1/test-mode/status-summary (one SQL query). */
export function useTestModeKpiCounts(): { counts: TestModeKpiCounts; loading: boolean } {
    const summary = useGenericQuery<StatusSummaryResponse>(
        SUMMARY_QUERY,
        'test-mode/status-summary',
        SUMMARY_POLL_MS,
        undefined,
        true,
        undefined,
        !FIXTURES,
    );

    if (FIXTURES) {
        return {
            counts: {
                inTestMode: FIXTURE_ROWS.filter((r) => Boolean(r.inTest)).length,
                completed: FIXTURE_ROWS.filter((r) => Boolean(r.testedApp)).length,
                notTested: FIXTURE_ROWS.filter((r) => Boolean(r.inTest) && !r.testedApp).length,
                flagged: FIXTURE_ROWS.filter((r) => Boolean(r.exitedWithoutTest) || Boolean(r.pairedDeviceUntested)).length,
            },
            loading: false,
        };
    }

    return {
        counts: {
            inTestMode: summary.data?.inTestMode,
            completed: summary.data?.completed,
            notTested: summary.data?.notTested,
            // null while the endpoint is unavailable → tile shows an explicit
            // "unavailable" state instead of a wrong zero.
            flagged: summary.data?.flagged ?? null,
        },
        loading: summary.isLoading,
    };
}

// ─── Onboarding-progress report (Zone 3, warehouse-backed) ─────────────────

const REPORT_QUERY = 'test-mode-report';

export const buildReportUrl = (params: ReportParams): string => {
    const qs = new URLSearchParams();
    qs.set('period', params.period);
    if (params.groupId) qs.set('groupId', params.groupId);
    if (params.teamId) qs.set('teamId', params.teamId);
    return `test-mode/report?${qs.toString()}`;
};

export function useTestModeReport(params: ReportParams) {
    const query = useGenericQuery<WhoTestedRow[]>(
        REPORT_QUERY,
        buildReportUrl(params),
        undefined,
        undefined,
        true,
        undefined,
        !FIXTURES,
    );
    if (FIXTURES) {
        return { data: fixtureReportRows(params), isLoading: false, isError: false };
    }
    return { data: query.data, isLoading: query.isLoading, isError: query.isError };
}

// ─── Admin actions (TM-8 / D13) ─────────────────────────────────────────────
// Entry is a DIRECT action (bulk re-enroll); exit is only ever a REQUEST the
// user confirms on their device. Both route through pom-2-api, which
// delegates to node-api's lifecycle authority.

const invalidateStatusList = () => queryClient.invalidateQueries([STATUS_LIST_QUERY]);

export interface ReEnrollOutcome {
    results: { userId: string; ok: boolean; sessionId?: string; error?: string }[];
    rejected: string[];
}

/** POST /v1/test-mode/re-enroll — put the selected users into test mode. */
export function useBulkReEnroll() {
    return useMutation(
        async (userIds: string[]): Promise<ReEnrollOutcome> => {
            if (FIXTURES) {
                await new Promise((r) => setTimeout(r, 500));
                return { results: userIds.map((userId) => ({ userId, ok: true })), rejected: [] };
            }
            const body: ReEnrollRequest = { userIds, windowType: 'onboarding' };
            const { data } = await api.post<DefaultRes<ReEnrollOutcome>, AxiosResponse<DefaultRes<ReEnrollOutcome>>>(
                '/test-mode/re-enroll',
                body,
            );
            return data.result;
        },
        { onSuccess: invalidateStatusList },
    );
}

export interface RequestExitOutcome {
    ok: string[];
    failed: string[];
}

/**
 * POST /v1/test-mode/request-exit — one bulk call (mirrors /re-enroll); the
 * backend relays to node-api per user with bounded concurrency and reports a
 * per-user outcome (partial success is possible and surfaced).
 */
export function useRequestExit() {
    return useMutation(
        async (userIds: string[]): Promise<RequestExitOutcome> => {
            if (FIXTURES) {
                await new Promise((r) => setTimeout(r, 500));
                return { ok: [...userIds], failed: [] };
            }
            const body: RequestExitRequest = { userIds };
            const { data } = await api.post<DefaultRes<RequestExitOutcome>, AxiosResponse<DefaultRes<RequestExitOutcome>>>(
                '/test-mode/request-exit',
                body,
            );
            return data.result;
        },
        { onSuccess: invalidateStatusList },
    );
}
